/**
 * LandingPage — public welcome page shown to logged-out users at "/" and "/welcome".
 *
 * - Branded with current admin-configured app_name, tagline, logo, theme color
 * - If ?invite=CODE present, fetches a preview and renders an inviter-personalised
 *   "Join {group_name}" hero with the inviter's name.
 * - CTAs route to /signup (preserving the invite code) and /login.
 *
 * Funnel: WhatsApp invite link → tap → branded landing → tap "Sign up" → signup
 *  → after successful signup, app boots → PWAInstallPrompt fires 1.5s later.
 */
import { useEffect, useState } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Sparkles, Receipt, Wallet, Users, Zap, Shield,
  ArrowRight, Smartphone, Check, LogIn, Download,
} from 'lucide-react';
import { useBranding, brandingAssetUrl } from '../context/BrandingContext';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import InstallCTASection from '../components/InstallCTASection';

const BACKEND = process.env.REACT_APP_BACKEND_URL || '';

const fadeUp = {
  initial: { opacity: 0, y: 16 },
  animate: { opacity: 1, y: 0 },
};

export default function LandingPage() {
  const { branding } = useBranding();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const inviteCode = params.get('invite') || params.get('i');

  const [invitePreview, setInvitePreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(!!inviteCode);

  // Fetch invite preview if landing page hit with ?invite=CODE
  useEffect(() => {
    if (!inviteCode) return;
    let cancel = false;
    fetch(`${BACKEND}/api/invites/${encodeURIComponent(inviteCode)}/preview`)
      .then((r) => r.json())
      .then((data) => {
        if (!cancel) setInvitePreview(data);
      })
      .catch(() => { if (!cancel) setInvitePreview({ valid: false, reason: 'Could not load invite' }); })
      .finally(() => { if (!cancel) setPreviewLoading(false); });
    return () => { cancel = true; };
  }, [inviteCode]);

  const signupHref = inviteCode ? `/signup?invite=${encodeURIComponent(inviteCode)}` : '/signup';
  const loginHref = inviteCode ? `/login?invite=${encodeURIComponent(inviteCode)}` : '/login';

  const groupTypeLabel = (t) => ({ sports: 'Sports group', trip: 'Trip', event: 'Event', daily: 'Household group', custom: 'Household group' }[t] || 'Group');

  return (
    <div className="min-h-screen bg-background overflow-x-hidden" data-testid="landing-page">
      {/* Top bar */}
      <header className="sticky top-0 z-30 bg-background/85 backdrop-blur-xl border-b border-border">
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            {branding.logo_url && (
              <img
                src={brandingAssetUrl(branding.logo_url)}
                alt={branding.app_name}
                className="w-8 h-8 rounded-md object-contain"
                data-testid="landing-logo"
                onError={(e) => { e.currentTarget.style.display = 'none'; }}
              />
            )}
            <span className="font-display text-lg font-bold tracking-tight" data-testid="landing-brand-name">
              {branding.app_name}
            </span>
          </div>
          <Link
            to={loginHref}
            className="text-sm font-semibold text-primary hover:underline"
            data-testid="landing-login-link"
          >
            Log in
          </Link>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 pb-20">
        {/* Invite Banner (only when ?invite=…) */}
        {inviteCode && (
          <motion.div
            {...fadeUp}
            transition={{ duration: 0.4 }}
            className="mt-5"
          >
            <Card
              className="p-4 sm:p-5 border-2 rounded-2xl bg-accent/10 border-accent/40 relative overflow-hidden"
              data-testid="invite-preview"
            >
              <Sparkles className="absolute top-3 right-3 w-5 h-5 text-accent" />
              {previewLoading ? (
                <p className="text-sm text-muted-foreground">Loading invite…</p>
              ) : invitePreview?.valid ? (
                <>
                  <p className="text-xs uppercase tracking-wider font-bold text-accent">
                    You're invited
                  </p>
                  <h2 className="font-display text-xl sm:text-2xl font-bold mt-1" data-testid="invite-group-name">
                    {invitePreview.inviter_name
                      ? `${invitePreview.inviter_name} added you to ${invitePreview.group_name}`
                      : `Join ${invitePreview.group_name}`}
                  </h2>
                  <p className="text-sm text-muted-foreground mt-1">
                    {groupTypeLabel(invitePreview.group_type)} · {invitePreview.member_count} member
                    {invitePreview.member_count === 1 ? '' : 's'} already inside
                  </p>
                </>
              ) : (
                <>
                  <p className="text-xs uppercase tracking-wider font-bold text-muted-foreground">
                    Invite link
                  </p>
                  <h2 className="font-display text-lg font-bold mt-1">{invitePreview?.reason || 'Invalid invite'}</h2>
                  <p className="text-xs text-muted-foreground mt-1">
                    You can still sign up and ask the group admin for a fresh invite.
                  </p>
                </>
              )}
            </Card>
          </motion.div>
        )}

        {/* Hero */}
        <section className="pt-10 pb-8 sm:pt-16 sm:pb-12 text-center">
          <motion.div
            {...fadeUp}
            transition={{ duration: 0.5 }}
            className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 border border-primary/20 mb-5"
          >
            <span className="w-1.5 h-1.5 rounded-full bg-primary animate-pulse" />
            <span className="text-xs font-bold uppercase tracking-wider text-primary">
              {branding.tagline || 'Track expenses with friends'}
            </span>
          </motion.div>

          <motion.h1
            {...fadeUp}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.05]"
            data-testid="landing-hero-title"
          >
            Group expenses, <span className="text-primary">finally</span> sane.
          </motion.h1>

          <motion.p
            {...fadeUp}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="mt-4 text-base sm:text-lg text-muted-foreground max-w-xl mx-auto"
          >
            Add it once, split it fair, settle it instantly. No spreadsheets,
            no awkward "who paid last time?" — just clean numbers everyone trusts.
          </motion.p>

          <motion.div
            {...fadeUp}
            transition={{ duration: 0.5, delay: 0.3 }}
            className="mt-7 flex flex-col sm:flex-row gap-3 justify-center"
          >
            <Button
              onClick={() => navigate(signupHref)}
              size="lg"
              className="rounded-full h-12 px-7 font-bold text-base press-scale"
              data-testid="landing-cta-signup"
            >
              {invitePreview?.valid
                ? `Join ${invitePreview.group_name}`
                : `Get started — it's free`}
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
            <Button
              onClick={() => navigate(loginHref)}
              size="lg"
              variant="outline"
              className="rounded-full h-12 px-6 font-bold text-base border-2"
              data-testid="landing-cta-login"
            >
              <LogIn className="w-4 h-4 mr-2" />
              I have an account
            </Button>
          </motion.div>

          <p className="mt-5 text-xs text-muted-foreground">
            Works in your browser · Installs to home screen · No app store needed
          </p>
        </section>

        {/* iter125c — prominent Android + iPhone install CTAs */}
        <InstallCTASection />

        {/* Feature cards */}
        <section className="grid sm:grid-cols-3 gap-3 sm:gap-4 my-6">
          <FeatureCard
            icon={Receipt}
            title="Add once, split fair"
            body="Equal, custom or share-based splits with peer or admin approvals so nobody pays for surprise charges."
            delay={0.1}
            testId="feature-split"
          />
          <FeatureCard
            icon={Wallet}
            title="One pot, zero awkward"
            body="Group fund + personal money ledger keeps team funds and individual debts cleanly separate."
            delay={0.2}
            testId="feature-fund"
          />
          <FeatureCard
            icon={Zap}
            title="Settle in one tap"
            body="Quick-Settle figures out the smallest set of transfers to zero everyone out. Instant peace."
            delay={0.3}
            testId="feature-settle"
          />
        </section>

        {/* How it works */}
        <section className="my-10">
          <motion.h3
            {...fadeUp}
            transition={{ duration: 0.4 }}
            className="font-display text-2xl sm:text-3xl font-bold text-center mb-6"
          >
            Up and running in 60 seconds
          </motion.h3>
          <div className="space-y-3">
            {[
              {
                n: 1,
                t: 'Sign up with your phone',
                b: 'Phone + password. No emails to verify, no friction.',
                icon: Smartphone,
              },
              {
                n: 2,
                t: 'Join your group',
                b: invitePreview?.valid
                  ? `Tap the green button above and you're in.`
                  : 'Use an invite link or create a new group from scratch.',
                icon: Users,
              },
              {
                n: 3,
                t: 'Add to home screen',
                b: 'One tap and it lives on your phone like a real app — full-screen, push notifications, offline.',
                icon: Download,
              },
            ].map((step, i) => (
              <motion.div
                key={step.n}
                {...fadeUp}
                transition={{ duration: 0.35, delay: 0.05 * i }}
                className="flex items-start gap-3"
              >
                <div className="shrink-0 w-9 h-9 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-display font-bold">
                  {step.n}
                </div>
                <Card className="flex-1 p-3 border-2 rounded-xl">
                  <div className="flex items-start gap-3">
                    <step.icon className="w-5 h-5 text-primary mt-0.5 shrink-0" />
                    <div>
                      <p className="font-bold">{step.t}</p>
                      <p className="text-sm text-muted-foreground mt-0.5">{step.b}</p>
                    </div>
                  </div>
                </Card>
              </motion.div>
            ))}
          </div>
        </section>

        {/* Trust strip */}
        <section className="my-8">
          <Card className="p-4 sm:p-5 border-2 rounded-2xl bg-muted/40">
            <div className="flex items-start gap-3">
              <Shield className="w-5 h-5 text-positive shrink-0 mt-0.5" />
              <div className="space-y-1.5 text-sm">
                <p className="font-bold">Built for groups, designed for trust</p>
                <ul className="text-muted-foreground space-y-1 text-[13px]">
                  <li className="flex items-center gap-2">
                    <Check className="w-3.5 h-3.5 text-positive shrink-0" />
                    Every change is audit-logged and reversible
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="w-3.5 h-3.5 text-positive shrink-0" />
                    Daily backups, self-hosted error logs
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="w-3.5 h-3.5 text-positive shrink-0" />
                    Works offline — your data is never lost
                  </li>
                </ul>
              </div>
            </div>
          </Card>
        </section>

        {/* Final CTA */}
        <section className="text-center my-10">
          <Button
            onClick={() => navigate(signupHref)}
            size="lg"
            className="rounded-full h-12 px-8 font-bold text-base press-scale"
            data-testid="landing-cta-signup-bottom"
          >
            {invitePreview?.valid
              ? `Join ${invitePreview.group_name}`
              : 'Create my free account'}
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
          <p className="mt-3 text-xs text-muted-foreground">
            Already have an account? <Link to={loginHref} className="text-primary font-semibold hover:underline">Log in</Link>
          </p>
        </section>

        {/* Footer */}
        <footer className="text-center text-xs text-muted-foreground border-t border-border pt-6 pb-4">
          <p className="font-display font-bold text-foreground text-sm">{branding.app_name}</p>
          <p className="mt-1">{branding.footer_text || 'Track expenses with friends, hassle-free'}</p>
        </footer>
      </main>
    </div>
  );
}

function FeatureCard({ icon: Icon, title, body, delay = 0, testId }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.45, delay }}
      data-testid={testId}
    >
      <Card className="p-4 sm:p-5 border-2 rounded-2xl h-full lift-hover">
        <div className="w-10 h-10 rounded-xl bg-primary/15 flex items-center justify-center mb-3">
          <Icon className="w-5 h-5 text-primary" strokeWidth={2.5} />
        </div>
        <h4 className="font-display font-bold text-base">{title}</h4>
        <p className="text-sm text-muted-foreground mt-1.5 leading-relaxed">{body}</p>
      </Card>
    </motion.div>
  );
}
